import { supabase } from '../client';
import { requireContext, type ActiveContext } from '../activeContext';
import type { Database, Json } from '../generated';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';

// ---- types ---------------------------------------------------------------

export type ContractTemplate = Database['contracts']['Tables']['contract_templates']['Row'];
export type ContractTemplateInsert = Database['contracts']['Tables']['contract_templates']['Insert'];
export type ContractTemplateUpdate = Database['contracts']['Tables']['contract_templates']['Update'];
export type ContractTemplateVersion =
  Database['contracts']['Tables']['contract_template_versions']['Row'];
export type ProposalContract = Database['contracts']['Tables']['proposal_contracts']['Row'];
export type ProposalContractAcceptance =
  Database['contracts']['Tables']['proposal_contract_acceptances']['Row'];
export type OrderContractStatus =
  Database['contracts']['Views']['v_order_contract_status']['Row'];

// ---- templates -----------------------------------------------------------

export interface ListContractTemplatesInput extends KeysetInput {
  includeArchived?: boolean;
  search?: string;
}

/**
 * Paginated keyset list of contract templates in the active store. The
 * default `includeArchived: false` matches the partial composite index
 * `contracts_templates_list_keyset_idx` (where archived_at is null).
 */
export async function listContractTemplates(
  ctx: ActiveContext,
  input: ListContractTemplatesInput = {},
): Promise<ListResult<ContractTemplate>> {
  const { orgId, storeId } = requireContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  let q = supabase()
    .schema('contracts')
    .from('contract_templates')
    .select('*', selectCountOption(input.countMode))
    .eq('org_id', orgId)
    .eq('store_id', storeId);

  if (!input.includeArchived) q = q.is('archived_at', null);
  if (input.search?.trim()) {
    const s = `%${input.search.trim()}%`;
    q = q.or(`name.ilike.${s},slug.ilike.${s},description.ilike.${s}`);
  }

  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ContractTemplate[];
  return {
    rows,
    nextCursor: buildNextCursor(rows as unknown as Array<Record<string, unknown>>, pageSize, 'updated_at'),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

/**
 * One-shot small fetch for dashboard widgets that want the most-recent N
 * contract templates.
 */
export async function listContractTemplatesTop(
  ctx: ActiveContext,
  input: Omit<ListContractTemplatesInput, 'cursor' | 'countMode'> & { limit?: number } = {},
): Promise<ContractTemplate[]> {
  const { rows } = await listContractTemplates(ctx, {
    ...input,
    pageSize: input.limit ?? 5,
    countMode: 'none',
  });
  return rows;
}

export async function getContractTemplate(
  ctx: ActiveContext,
  id: string,
): Promise<ContractTemplate | null> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('contracts')
    .from('contract_templates')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export interface CreateContractTemplateInput {
  name: string;
  slug: string;
  description?: string | null;
  requireSignature?: boolean;
  defaultAttach?: boolean;
}

export async function createContractTemplate(
  ctx: ActiveContext,
  input: CreateContractTemplateInput,
): Promise<ContractTemplate> {
  const { orgId, storeId } = requireContext(ctx);
  const row: ContractTemplateInsert = {
    org_id: orgId,
    store_id: storeId,
    name: input.name.trim(),
    slug: input.slug.trim().toLowerCase(),
    description: input.description ?? undefined,
    require_signature: input.requireSignature ?? true,
    default_attach: input.defaultAttach ?? false,
  };
  const { data, error } = await supabase()
    .schema('contracts')
    .from('contract_templates')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  return data as ContractTemplate;
}

export interface UpdateContractTemplateInput {
  id: string;
  name?: string;
  description?: string | null;
  requireSignature?: boolean;
  defaultAttach?: boolean;
  archived?: boolean;
}

export async function updateContractTemplate(
  ctx: ActiveContext,
  input: UpdateContractTemplateInput,
): Promise<ContractTemplate> {
  requireContext(ctx);
  const patch: ContractTemplateUpdate = {};
  if (input.name !== undefined) patch.name = input.name.trim();
  if (input.description !== undefined) patch.description = input.description;
  if (input.requireSignature !== undefined) patch.require_signature = input.requireSignature;
  if (input.defaultAttach !== undefined) patch.default_attach = input.defaultAttach;
  if (input.archived !== undefined) {
    patch.archived_at = input.archived ? new Date().toISOString() : null;
  }
  const { data, error } = await supabase()
    .schema('contracts')
    .from('contract_templates')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ContractTemplate;
}

// ---- versions ------------------------------------------------------------

export async function listTemplateVersions(
  ctx: ActiveContext,
  templateId: string,
): Promise<ContractTemplateVersion[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('contracts')
    .from('contract_template_versions')
    .select('*')
    .eq('template_id', templateId)
    .order('version_number', { ascending: false })
    .limit(500);
  if (error) throw error;
  return data ?? [];
}

export async function getCurrentTemplateVersion(
  ctx: ActiveContext,
  templateId: string,
): Promise<ContractTemplateVersion | null> {
  requireContext(ctx);
  const tpl = await getContractTemplate(ctx, templateId);
  if (!tpl || tpl.current_version === 0) return null;
  const { data, error } = await supabase()
    .schema('contracts')
    .from('contract_template_versions')
    .select('*')
    .eq('template_id', templateId)
    .eq('version_number', tpl.current_version)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export interface PublishTemplateVersionInput {
  templateId: string;
  bodyMd: string;
  changelog?: string | null;
}

export async function publishTemplateVersion(
  ctx: ActiveContext,
  input: PublishTemplateVersionInput,
): Promise<ContractTemplateVersion> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('contracts')
    .rpc('rpc_publish_template_version', {
      p_template_id: input.templateId,
      p_body_md: input.bodyMd,
      p_changelog: input.changelog ?? undefined,
    });
  if (error) throw error;
  return data as ContractTemplateVersion;
}

// ---- proposal attachments ------------------------------------------------

export async function listProposalContracts(
  ctx: ActiveContext,
  orderId: string,
): Promise<ProposalContract[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('contracts')
    .from('proposal_contracts')
    .select('*')
    .eq('order_id', orderId)
    .order('position', { ascending: true })
    .limit(100);
  if (error) throw error;
  return data ?? [];
}

export async function getOrderContractStatus(
  ctx: ActiveContext,
  orderId: string,
): Promise<OrderContractStatus | null> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('contracts')
    .from('v_order_contract_status')
    .select('*')
    .eq('order_id', orderId)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export async function listOrderAcceptances(
  ctx: ActiveContext,
  orderId: string,
): Promise<ProposalContractAcceptance[]> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('contracts')
    .from('proposal_contract_acceptances')
    .select('*')
    .eq('order_id', orderId)
    .order('accepted_at', { ascending: true })
    .limit(100);
  if (error) throw error;
  return data ?? [];
}

export interface AttachContractInput {
  orderId: string;
  templateId: string;
  position?: number;
}

export async function attachContractToOrder(
  ctx: ActiveContext,
  input: AttachContractInput,
): Promise<ProposalContract> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('contracts')
    .rpc('rpc_attach_template_to_order', {
      p_order_id: input.orderId,
      p_template_id: input.templateId,
      p_position: input.position ?? undefined,
    });
  if (error) throw error;
  return data as ProposalContract;
}

export async function detachContractFromOrder(
  ctx: ActiveContext,
  proposalContractId: string,
): Promise<void> {
  requireContext(ctx);
  const { error } = await supabase()
    .schema('contracts')
    .from('proposal_contracts')
    .delete()
    .eq('id', proposalContractId);
  if (error) throw error;
}

// ---- public (anon, by token) --------------------------------------------

export interface ProposalContractView {
  id: string;
  title: string;
  body_md: string;
  body_sha256: string;
  require_signature: boolean;
  position: number;
  accepted: boolean;
  accepted_at: string | null;
  acceptor_name: string | null;
}

export async function publicGetProposalContracts(
  token: string,
): Promise<ProposalContractView[]> {
  const { data, error } = await supabase()
    .schema('contracts')
    .rpc('rpc_get_proposal_contracts', { p_token: token });
  if (error) throw error;
  return (data as unknown as ProposalContractView[]) ?? [];
}

export interface PublicContractAcceptanceInput {
  proposalContractId: string;
  acceptorName: string;
  signatureText?: string;
  consentElectronicSignature: boolean;
}

/**
 * Direct anonymous accept (without the notify-proposal orchestration). The
 * client-facing approval flow normally goes through `publicApproveProposal`
 * in @pc/db/services/orders.ts which orchestrates accept + approve + email
 * in a single Edge Function call. This helper is exposed for tooling /
 * tests that need to record acceptances independently.
 */
export async function publicAcceptProposalContracts(
  token: string,
  acceptances: PublicContractAcceptanceInput[],
): Promise<{ ok: boolean; accepted_count: number }> {
  const payload = acceptances.map((a) => ({
    proposal_contract_id: a.proposalContractId,
    acceptor_name: a.acceptorName,
    signature_text: a.signatureText ?? a.acceptorName,
    consent_electronic_signature: a.consentElectronicSignature,
  }));
  const { data, error } = await supabase()
    .schema('contracts')
    .rpc('rpc_accept_proposal_contracts', {
      p_token: token,
      p_acceptances: payload as unknown as Json,
      p_acceptor_ip: undefined,
      p_acceptor_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      p_acceptor_locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
    });
  if (error) throw error;
  return data as { ok: boolean; accepted_count: number };
}
